import api from '../services/api';


/** Uma linha da listagem de locations, já normalizada. */
export interface LocationRow {
  /** Sempre preenchido: a API projeta `id`, outras rotas devolvem `_id`. */
  _id: string;
  code: string;
  name: string;
  type: string;
  parentLocationId?: string | null;
  parentLocationCode?: string | null;
  locationUri?: string | null;
  active?: boolean;
  /** Vista geográfica entre sites (#1523) */
  latitude?: number | null;
  longitude?: number | null;
  /** Planta baixa (#1523) — só um location raiz tem imagem */
  floorPlanImageUrl?: string | null;
  /** Posição relativa (0..1) sobre a planta do pai (#1523) */
  positionX?: number | null;
  positionY?: number | null;
}

/** Uma tag lida ao vivo numa zona (#1523 — conteúdo de zona clicável). */
export interface ZoneTag {
  epc: string;
  rssi?: number;
  decoded?: { scheme: string; gtin?: string; serial?: string };
  classification?: string;
}

export interface ZoneContents {
  locationId: string;
  deviceSerial: string | null;
  tags: ZoneTag[];
}

/** Um evento da linha do tempo de um EPC. */
export interface TraceabilityEvent {
  eventType: string;
  occurredAt?: string;
  source?: string;
  deviceSerial?: string;
  locationId?: string;
  zone?: string;
}

export interface TraceabilityPage {
  events: TraceabilityEvent[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export interface PickListItem {
  productId?: string;
  epc?: string;
  quantity: number;
  pickedQty: number;
  pickedEpcs: string[];
  gtin?: string;
  /** Preenchido quando o identificador do ERP não casou com nenhum produto. */
  unresolvedIdentifier?: string;
  unitPrice?: number;
  currency?: string;
}

export interface PickScanItem {
  epc?: string;
  gtin?: string;
  quantity?: number;
  idempotencyKey?: string;
}

/** Uma regra do de-para `reasonForPick` → ponto de entrega. */
export interface ReplenishmentDestinationMapping {
  _id?: string;
  reasonForPick: string;
  deliveryLocationId: string;
  sourceLocationId?: string;
  siteId?: string | null;
  active?: boolean;
}

export interface PickList {
  id: string;
  items: PickListItem[];
  /**
   * Ausente enquanto ninguém escolheu o ponto de entrega — uma reposição vinda
   * do ERP nasce sem destino quando não há de-para para o `reasonForPick`.
   */
  deliveryLocationId?: string;
  sourceLocationId?: string;
  assignedUserId?: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  conferenceMode: 'RFID' | 'BARCODE';
  origin?: 'CONSOLE' | 'EXTERNAL_API' | 'MOBILE';
  code?: string;
  externalTransactionId?: string;
  externalSource?: string;
  externalStatus?: string;
  reasonForPick?: string;
  bizLocationRaw?: string;
  requestingStaff?: string;
  pickerStaff?: string;
  /** De onde o estoque saiu ao concluir, agregado por local de origem. */
  completionEvidence?: {
    movedEpcCount: number;
    destinationLocationId: string;
    sources: Array<{ locationId: string | null; count: number }>;
  };
  notes?: string;
  createdAt?: string;
  startedAt?: string;
  completedAt?: string;
  cancelledAt?: string;
}

/**
 * A rota passou a paginar (#1388) e devolve `{ data, total, page, limit, pages,
 * hasMore }`. A normalização mora aqui, na fronteira, e aceita também o array
 * cru que a rota devolvia antes — ler o envelope errado é o defeito que custou
 * o #1352 e o #1369, e é barato não repetir.
 */
export function normalizeTraceability(payload: unknown): TraceabilityPage {
  if (Array.isArray(payload)) {
    const events = payload as TraceabilityEvent[];
    return { events, total: events.length, page: 1, limit: events.length, hasMore: false };
  }

  const corpo = (payload ?? {}) as Record<string, unknown>;
  const events = ((corpo.data ?? corpo.items ?? []) as TraceabilityEvent[]) || [];
  const limit = Number(corpo.limit) || events.length;
  const page = Number(corpo.page) || 1;
  const total = corpo.total !== undefined ? Number(corpo.total) : events.length;

  return {
    events,
    total,
    page,
    limit,
    hasMore: corpo.hasMore !== undefined ? Boolean(corpo.hasMore) : page * limit < total,
  };
}

export interface LocationsPage {
  rows: LocationRow[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

/**
 * `LocationsService.mapToEntity` projeta `id` e não expõe `_id`, e a tela lia
 * `_id` — então editar e excluir mandavam `undefined` no lugar do id, a `key`
 * de toda linha era undefined e o mapa de pais nunca resolvia (#1346).
 *
 * A normalização fica aqui, na fronteira, e não espalhada por cada uso. Aceita
 * as duas formas de propósito: há rotas no app que devolvem documento Mongoose
 * cru, com `_id`.
 */
export function normalizeLocations(payload: unknown): LocationsPage {
  const corpo = (payload ?? {}) as Record<string, unknown>;
  const lista: Record<string, unknown>[] = Array.isArray(payload)
    ? (payload as Record<string, unknown>[])
    : ((corpo.data ?? corpo.items ?? []) as Record<string, unknown>[]);

  const rows = lista.map((item) => ({
    ...item,
    _id: String(item._id ?? item.id ?? ''),
  })) as unknown as LocationRow[];

  const limit = Number(corpo.limit) || rows.length;
  const page = Number(corpo.page) || 1;
  const total = corpo.total !== undefined ? Number(corpo.total) : rows.length;

  return {
    rows,
    total,
    page,
    limit,
    // `hasMore` só vem na resposta paginada; fora dela, deduz do recorte.
    hasMore: corpo.hasMore !== undefined ? Boolean(corpo.hasMore) : page * limit < total,
  };
}


/**
 * O maior `limit` que a API aceita: `PaginationDto` declara `@Max(100)` e a
 * validação global rejeita acima disso com 400 — antes do `Math.min(limit, 100)`
 * do controller, que por isso nunca roda para esses valores.
 */
const LIMITE_POR_PAGINA = 100;

/** Teto de segurança: 5 páginas. Acima disso a tela precisa de busca, não de lista. */
const MAX_PAGINAS_LOCATIONS = 5;

/**
 * Todas as locations que couberem no teto, paginando dentro do limite da API.
 *
 * As telas pediam `limit: 200` numa única chamada, o que dava 400 sempre; como
 * as duas tratavam a falha degradando para lista vazia, o dropdown ficava vazio
 * e a tela dizia "nenhum local cadastrado" com o armazém cheio (#1393).
 */
export async function fetchAllLocations(): Promise<LocationRow[]> {
  const linhas: LocationRow[] = [];

  for (let page = 1; page <= MAX_PAGINAS_LOCATIONS; page += 1) {
    // eslint-disable-next-line no-await-in-loop -- páginas são sequenciais por natureza
    const pagina = await logisticsOpsApi.listLocations({ page, limit: LIMITE_POR_PAGINA });
    linhas.push(...pagina.rows);
    if (!pagina.hasMore || pagina.rows.length === 0) break;
  }

  return linhas;
}

export interface LocationNode extends LocationRow {
  children: LocationNode[];
}

/** Mesma normalização de `id` da listagem, aplicada em toda a árvore. */
export function normalizeTree(payload: unknown): LocationNode[] {
  const lista = Array.isArray(payload)
    ? (payload as Record<string, unknown>[])
    : (((payload as Record<string, unknown>)?.data ?? []) as Record<string, unknown>[]);

  return lista.map((no) => ({
    ...(no as unknown as LocationRow),
    _id: String(no._id ?? no.id ?? ''),
    children: normalizeTree(no.children ?? []),
  }));
}

/** Linha de `GET /romaneios` (#1825) — status já calculado das ordens pelo backend. */
export interface RomaneioSummary {
  code: string;
  manifest?: string;
  originUnit?: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED';
  orderCount: number;
  completedOrderCount: number;
  packageCount: number;
  unresolvedDeliveryUnits: string[];
  sourceFileName?: string;
  updatedAt?: string;
}

export interface RomaneioOrder {
  receivingDocumentId: string;
  documentCode?: string;
  nfeAccessKey: string;
  unidadeEntrega?: string;
  destinationLocationId?: string;
  status: string;
  expectedTotal: number;
  confirmedTotal: number;
}

export interface RomaneioDetail {
  code: string;
  manifest?: string;
  originUnit?: string;
  status: RomaneioSummary['status'];
  packageCount: number;
  manifestMismatchCount: number;
  unresolvedDeliveryUnits: string[];
  orders: RomaneioOrder[];
}

export interface RomaneioImportResult {
  romaneiosImported: number;
  ordersCreated: number;
  packagesImported: number;
  parseErrors: number;
  romaneios: {
    code: string;
    ordersCreated: number;
    packageCount: number;
    manifestMismatchCount: number;
    unresolvedDeliveryUnits: string[];
  }[];
}

export interface TmsLocationMapping {
  unidadeEntrega: string;
  locationId: string;
}

export const logisticsOpsApi = {
  // `model` é o nome do query param no controller; o campo do produto é `productModel`.
  listCatalog: (params?: {
    status?: string;
    q?: string;
    barcode?: string;
    categoryId?: string;
    model?: string;
    color?: string;
    size?: string;
  }) =>
    api.get('/product-catalog', { params }).then((r) => r.data),

  createProduct: (body: {
    barcodes: { type: string; value: string }[];
    description?: string;
    manufacturer?: string;
    categoryId?: string;
    productModel?: string;
    color?: string;
    size?: string;
    price?: number;
    vpn?: string;
  }) => api.post('/product-catalog', body).then((r) => r.data),

  // `null` limpa a dimensão; a chave ausente deixa o valor gravado como está.
  updateProduct: (
    id: string,
    patch: {
      description?: string;
      status?: string;
      categoryId?: string | null;
      productModel?: string | null;
      color?: string | null;
      size?: string | null;
      price?: number | null;
      vpn?: string | null;
    },
  ) =>
    api.patch(`/product-catalog/${id}`, patch).then((r) => r.data),

  /** Árvore GPC do tenant (#1526), plana: cada nó traz `parentId` e `level`. */
  listCategories: () => api.get('/category-hierarchy').then((r) => r.data),

  deleteProduct: (id: string) => api.delete(`/product-catalog/${id}`).then((r) => r.data),

  importCatalog: (products: any[]) =>
    api.post('/product-catalog/import', { products }).then((r) => r.data),

  importCatalogFile: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/product-catalog/import-file', form).then((r) => r.data);
  },

  listCommissioned: (params?: {
    objectType?: string;
    status?: string;
    gtin?: string;
    since?: string;
    limit?: number;
    skip?: number;
  }) =>
    api
      .get('/inventory', { params: { source: 'RFID', ...(params || {}) } })
      .then((r) => r.data),

  issueEpcs: (body: {
    barcode: string;
    quantity?: number;
    batchLot?: string;
    expirationDate?: string;
    /** Série do fabricante (GS1 AI 21). Com série, `quantity` precisa ser 1. */
    serialNumber?: string;
    locationId?: string;
    stationDeviceSerial?: string;
    /** Impressora RFID: o backend conduz leitura/gravação e devolve `seqId`. */
    printerId?: string;
  }) => api.post('/commissioning/issue', body).then((r) => r.data),

  /**
   * Etiquetas a partir da NF-e (XML ou DANFE em PDF): devolve o lote da nota,
   * uma linha por item, com o saldo de cada uma. Reimportar a mesma nota
   * devolve o mesmo lote com o que já foi emitido.
   */
  importNfeLabels: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/commissioning/nfe-labels/import', form).then((r) => r.data);
  },

  /** Emite `quantity` etiquetas de uma linha da NF-e; mesmo corpo de `issueEpcs` mais `line`. */
  issueNfeLabelLine: (
    batchId: string,
    nItem: number,
    body: { quantity: number; locationId?: string; printerId?: string },
  ) =>
    api
      .post(`/commissioning/nfe-labels/${encodeURIComponent(batchId)}/lines/${nItem}/issue`, body)
      .then((r) => r.data),

  bindTag: (body: { uid: string; epc: string; deviceSerial?: string; locationId?: string; documentId?: string }) =>
    api.post('/commissioning/bind', body).then((r) => r.data),

  /**
   * Escolha do operador numa leitura ambígua da estação (#1484, #1824). Ao
   * contrário de `bindTag`, tira o item da fila da estação — sem isso a
   * próxima leitura mira o item já resolvido e falha.
   */
  resolveStationAmbiguous: (deviceSerial: string, epc: string) =>
    api
      .post(`/commissioning/station/${encodeURIComponent(deviceSerial)}/resolve-ambiguous`, { epc })
      .then((r) => r.data),

  renderLabels: (
    items: {
      epc: string;
      /** Conteudo do QR quando difere do EPC — modo de identidade opaca. */
      qrContent?: string;
      batchLot?: string;
      expirationDate?: string;
      serialNumber?: string;
      description?: string;
    }[],
    labelFormat?: 'default' | '45x18' | '45x30',
    templateId?: string,
  ) => api.post('/commissioning/labels/zpl', { items, labelFormat, templateId }).then((r) => r.data),

  listUnboundEpcs: (params?: { deviceSerial?: string; limit?: number; skip?: number }) =>
    api.get('/commissioning/unbound-epcs', { params }).then((r) => r.data),

  bindUnboundEpc: (epc: string, uid: string) =>
    api.post(`/commissioning/unbound-epcs/${encodeURIComponent(epc)}/bind`, { uid }).then((r) => r.data),

  listAwaitingTag: (params?: {
    limit?: number;
    skip?: number;
    taggingState?: 'QUEUED' | 'IN_PROGRESS' | 'ENCODE_UNVERIFIED' | 'FAILED' | 'DONE';
  }) => api.get('/commissioning/awaiting-tag', { params }).then((r) => r.data),

  /** Reimprime com o MESMO identificador: emitir um novo transformaria uma
   *  etiqueta rasgada em um segundo item. */
  reprintAwaitingTag: (itemUid: string, labelFormat: 'default' | '45x30' = '45x30') =>
    api
      .post(`/commissioning/awaiting-tag/${encodeURIComponent(itemUid)}/reprint`, { labelFormat })
      .then((r) => r.data),

  getTraceability: (epc: string, params?: { page?: number; limit?: number }) =>
    api
      .get(`/traceability/${epc}`, { params })
      .then((r) => normalizeTraceability(r.data)),

  rebuildTraceability: (epc: string) =>
    api.post(`/traceability/${epc}/rebuild`).then((r) => r.data),

  listVerificationDocs: () => api.get('/verification/documents').then((r) => r.data),

  importVerificationDoc: (body: {
    name: string;
    operationType: string;
    lines: { productCode: string; expectedQty: number }[];
  }) => api.post('/verification/documents', body).then((r) => r.data),

  getVerificationSession: (id: string) =>
    api.get(`/verification/sessions/${id}`).then((r) => r.data),

  /** Fecha o documento à mão (#1829) — fechar a sessão no coletor não fecha o documento. */
  closeVerificationDoc: (id: string) =>
    api.post(`/verification/documents/${encodeURIComponent(id)}/close`).then((r) => r.data),

  /** CSV da sessão pelo cliente da API (mesmo motivo do #1830): os erros passam pelo interceptor. */
  getVerificationSessionCsv: (id: string): Promise<string> =>
    api
      .get(`/verification/sessions/${encodeURIComponent(id)}/export.csv`, { responseType: 'text' })
      .then((r) => r.data),

  getFeatureConfig: () => api.get('/features/config').then((r) => r.data),

  listLocations: (params?: {
    page?: number;
    limit?: number;
    type?: string;
    search?: string;
    parentLocationId?: string;
  }): Promise<LocationsPage> => api.get('/locations', { params }).then((r) => normalizeLocations(r.data)),

  getLocationTree: () => api.get('/locations/tree').then((r) => r.data),

  /** Filhas diretas. A tela usa para dizer o que a exclusão arrasta junto. */
  getLocationChildren: (id: string): Promise<LocationRow[]> =>
    api.get(`/locations/${id}/children`).then((r) => normalizeLocations(r.data).rows),

  createLocation: (body: {
    code: string;
    name: string;
    type: string;
    parentLocationId?: string;
    metadata?: Record<string, unknown>;
    latitude?: number;
    longitude?: number;
    positionX?: number;
    positionY?: number;
  }) => api.post('/locations', body).then((r) => r.data),

  updateLocation: (
    id: string,
    patch: {
      code?: string; name?: string; type?: string; parentLocationId?: string | null; active?: boolean;
      latitude?: number; longitude?: number; positionX?: number; positionY?: number;
    },
  ) => api.put(`/locations/${id}`, patch).then((r) => r.data),

  /** #1523 — upload da planta baixa (só location raiz, admin). */
  uploadFloorPlan: (id: string, file: File): Promise<LocationRow> => {
    const form = new FormData();
    form.append('file', file);
    return api.post(`/locations/${id}/floor-plan`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data);
  },

  /** #1523 — conteúdo ao vivo de uma zona clicável (presença via tag-zone). */
  getZoneContents: (id: string): Promise<ZoneContents> =>
    api.get(`/locations/${id}/zone-contents`).then((r) => r.data),

  /**
   * #1523 — a rota exige o mesmo Bearer do resto da API, então `<img src>`
   * direto não serve (não carrega header nenhum); busca como blob e devolve
   * um object URL. Chamador libera com `URL.revokeObjectURL` ao desmontar.
   */
  getFloorPlanImageBlobUrl: (id: string): Promise<string> =>
    api.get(`/locations/${id}/floor-plan-image`, { responseType: 'blob' })
      .then((r) => URL.createObjectURL(r.data)),

  deleteLocation: (id: string) => api.delete(`/locations/${id}`).then((r) => r.data),

  tagLocation: (id: string, body: { captureMethod: 'RFID' | 'QR_CODE' | 'BARCODE'; rawValue: string }) =>
    api.post(`/locations/${id}/tag`, body).then((r) => r.data),

  /**
   * Hierarquia inteira, em árvore. Cada nó traz `children`; o `_id` é
   * normalizado como na listagem, porque a API projeta `id`.
   */
  getLocationTreeNormalized: (): Promise<LocationNode[]> =>
    api.get('/locations/tree').then((r) => normalizeTree(r.data)),

  importLocationsFile: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/locations/import', form).then((r) => r.data);
  },

  listReceivingDocuments: (params?: { status?: string; since?: string; until?: string }) =>
    api.get('/receiving/documents', { params }).then((r) => r.data),

  getReceivingDocument: (id: string) =>
    api.get(`/receiving/documents/${id}`).then((r) => r.data),

  importNfe: (file: File, destinationLocationId: string, overwrite?: boolean) => {
    const form = new FormData();
    form.append('file', file);
    form.append('destinationLocationId', destinationLocationId);
    if (overwrite) form.append('overwrite', 'true');
    return api.post('/receiving/documents/import-nfe', form).then((r) => r.data);
  },

  createReceivingDocument: (body: {
    destinationLocationId: string;
    items?: { productId?: string; epc?: string; quantity: number }[];
  }) => api.post('/receiving/documents', body).then((r) => r.data),

  updateReceivingDocument: (
    id: string,
    patch: {
      destinationLocationId?: string;
      items?: { productId?: string; epc?: string; quantity: number }[];
    },
  ) => api.patch(`/receiving/documents/${id}`, patch).then((r) => r.data),

  deleteReceivingDocument: (id: string) =>
    api.delete(`/receiving/documents/${id}`).then((r) => r.data),

  getReceivingDocumentByCode: (code: string) =>
    api.get(`/receiving/documents/by-code/${encodeURIComponent(code)}`).then((r) => r.data),

  scanReceivingDocument: (
    id: string,
    payload: { epc: string; barcode?: string; deviceSerial?: string; batchLot?: string; expirationDate?: string },
  ) => api.post(`/receiving/documents/${id}/scan`, payload).then((r) => r.data),

  /** `reason` só vale no fechamento forçado (#1839); a web sempre manda quando força. */
  completeReceivingDocument: (id: string, override = false, reason?: string) =>
    api.post(`/receiving/documents/${id}/complete`, reason ? { override, reason } : { override }).then((r) => r.data),

  listShippingDocuments: (params?: { status?: string; since?: string; until?: string }) =>
    api.get('/shipping/documents', { params }).then((r) => r.data),

  getShippingDocument: (id: string) =>
    api.get(`/shipping/documents/${id}`).then((r) => r.data),

  createShippingDocument: (body: {
    originLocationId: string;
    /** Opcional: com destino, concluir gera o recebimento lá (#1499/#1546). */
    destinationLocationId?: string;
    items?: { productId?: string; epc?: string; quantity: number }[];
  }) => api.post('/shipping/documents', body).then((r) => r.data),

  updateShippingDocument: (
    id: string,
    patch: {
      originLocationId?: string;
      /** `null` tira o destino gravado; omitir não mexe nele (#1546). */
      destinationLocationId?: string | null;
      items?: { productId?: string; epc?: string; quantity: number }[];
    },
  ) => api.patch(`/shipping/documents/${id}`, patch).then((r) => r.data),

  deleteShippingDocument: (id: string) =>
    api.delete(`/shipping/documents/${id}`).then((r) => r.data),

  getShippingDocumentByCode: (code: string) =>
    api.get(`/shipping/documents/by-code/${encodeURIComponent(code)}`).then((r) => r.data),

  /**
   * CSV de conferência de UM documento, gerado no servidor (#1469, #1830):
   * uma linha por EPC conferido, por linha em falta e por EPC recebido fora do
   * documento. Pelo cliente da API (não por link): era para levar a
   * `x-api-key`, que o dashboard não manda mais (#1778); segue pelo cliente
   * para o 401 de sessão expirada passar pelo interceptor.
   */
  getDocumentConferenceCsv: (kind: 'receiving' | 'shipping', id: string): Promise<string> =>
    api
      .get(`/${kind}/documents/${encodeURIComponent(id)}/export.csv`, { responseType: 'text' })
      .then((r) => r.data),

  // Romaneio TMS (#1825). A conferência (`scan`, `scan-batch`) é do app móvel.
  listRomaneios: (limit?: number): Promise<RomaneioSummary[]> =>
    api.get('/romaneios', { params: limit ? { limit } : undefined }).then((r) => r.data),

  getRomaneio: (code: string): Promise<RomaneioDetail> =>
    api.get(`/romaneios/by-code/${encodeURIComponent(code)}`).then((r) => r.data),

  importRomaneiosTms: (file: File): Promise<RomaneioImportResult> => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/romaneios/import-tms', form).then((r) => r.data);
  },

  listTmsLocationMappings: (): Promise<TmsLocationMapping[]> =>
    api.get('/romaneios/location-mappings').then((r) => r.data),

  upsertTmsLocationMapping: (body: TmsLocationMapping): Promise<void> =>
    api.post('/romaneios/location-mappings', body).then((r) => r.data),

  // Romaneio criado no console (#1837).
  createRomaneio: (originUnit?: string): Promise<RomaneioDetail> =>
    api.post('/romaneios', originUnit ? { originUnit } : {}).then((r) => r.data),

  addRomaneioOrder: (code: string, body: { nfeAccessKey: string; destinationLocationId: string }): Promise<RomaneioDetail> =>
    api.post(`/romaneios/${encodeURIComponent(code)}/orders`, body).then((r) => r.data),

  removeRomaneioOrder: (code: string, receivingDocumentId: string): Promise<RomaneioDetail> =>
    api
      .delete(`/romaneios/${encodeURIComponent(code)}/orders/${encodeURIComponent(receivingDocumentId)}`)
      .then((r) => r.data),

  deactivateRomaneio: (code: string): Promise<void> =>
    api.post(`/romaneios/${encodeURIComponent(code)}/deactivate`).then(() => undefined),

  /**
   * Divergência expedido × recebido por produto nos vínculos CD→loja (#1499,
   * #1828). Só expedições concluídas sem override e com destino; devolve só
   * as que têm diferença — inclusive as cujo recebimento ainda não terminou.
   */
  getInTransitDiscrepancies: (): Promise<Array<{
    shippingDocumentId: string;
    shippingDocumentCode?: string;
    destinationLocationId: string;
    receivingDocumentId?: string;
    receivingStatus?: string;
    items: Array<{ productId?: string; shippedQty: number; receivedQty: number; missingQty: number }>;
  }>> => api.get('/shipping/documents/in-transit-discrepancies').then((r) => r.data),

  scanShippingDocument: (id: string, payload: { epc: string; deviceSerial?: string }) =>
    api.post(`/shipping/documents/${id}/scan`, payload).then((r) => r.data),

  /** `reason` só vale no fechamento forçado (#1839); a web sempre manda quando força. */
  completeShippingDocument: (id: string, override = false, reason?: string) =>
    api.post(`/shipping/documents/${id}/complete`, reason ? { override, reason } : { override }).then((r) => r.data),

  getCategoryConfig: (): Promise<{ tenantId: string; maxLevels: number }> =>
    api.get('/category-hierarchy/config').then((r) => r.data),

  setCategoryConfig: (maxLevels: number) =>
    api.put('/category-hierarchy/config', { maxLevels }).then((r) => r.data),

  // Árvore inteira: usar `listCategories` (#1545), acima — mesma rota, não duplicar.

  createCategoryNode: (body: { name: string; level: 1 | 2 | 3 | 4; parentId?: string; gpcCode?: string; attributes?: string[] }) =>
    api.post('/category-hierarchy', body).then((r) => r.data),

  updateCategoryNode: (id: string, patch: { name?: string; parentId?: string }) =>
    api.patch(`/category-hierarchy/${id}`, patch).then((r) => r.data),

  deleteCategoryNode: (id: string) =>
    api.delete(`/category-hierarchy/${id}`).then((r) => r.data),

  importCategoryTree: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/category-hierarchy/import', form).then((r) => r.data);
  },

  listInventoryByLocation: (locationId: string, params?: { search?: string; skip?: number; limit?: number }) =>
    api.get(`/inventory/location/${locationId}`, { params }).then((r) => r.data),

  getInventorySummary: (params?: { locationId?: string; gtin?: string }) =>
    api.get('/inventory/summary', { params }).then((r) => r.data),

  listStaleInventory: (params?: { thresholdMinutes?: number; limit?: number; skip?: number }) =>
    api.get('/inventory/stale', { params }).then((r) => r.data),

  listCycleCountSessions: (params?: { locationId?: string; skip?: number; limit?: number }) =>
    api.get('/inventory/cycle-count-sessions', { params }).then((r) => r.data),

  getCycleCountSession: (id: string) =>
    api.get(`/inventory/cycle-count-sessions/${id}`).then((r) => r.data),

  listPickLists: (params?: {
    status?: string;
    assignedUserId?: string;
    origin?: string;
    reasonForPick?: string;
    since?: string;
    until?: string;
    skip?: number;
    limit?: number;
  }) => api.get('/pick-lists', { params }).then((r) => r.data),

  assignPickList: (id: string, assignedUserId?: string) =>
    api.patch(`/pick-lists/${id}/assign`, { assignedUserId }).then((r) => r.data),

  setPickListDeliveryLocation: (id: string, deliveryLocationId: string) =>
    api.patch(`/pick-lists/${id}/delivery-location`, { deliveryLocationId }).then((r) => r.data),

  scanPickListBatch: (id: string, items: PickScanItem[]) =>
    api.post(`/pick-lists/${id}/scan-batch`, { items }).then((r) => r.data),

  listReplenishmentMappings: () =>
    api.get('/replenishment/destination-mappings').then((r) => r.data),

  upsertReplenishmentMapping: (body: {
    reasonForPick: string;
    deliveryLocationId: string;
    sourceLocationId?: string;
    siteId?: string;
    active?: boolean;
  }) => api.put('/replenishment/destination-mappings', body).then((r) => r.data),

  createPickList: (body: {
    deliveryLocationId?: string;
    sourceLocationId?: string;
    assignedUserId?: string;
    conferenceMode?: 'RFID' | 'BARCODE';
    notes?: string;
    items: { productId?: string; epc?: string; quantity: number }[];
  }) => api.post('/pick-lists', body).then((r) => r.data),

  getPickList: (id: string) => api.get(`/pick-lists/${id}`).then((r) => r.data),

  completePickList: (id: string, partial = false) =>
    api.post(`/pick-lists/${id}/complete`, { partial }).then((r) => r.data),

  cancelPickList: (id: string) => api.post(`/pick-lists/${id}/cancel`).then((r) => r.data),

  // #1551 — catálogo de status de inventário (identifier imutável, isSystem trava rename/delete)
  listStatusCatalog: () => api.get('/inventory-status-catalog').then((r) => r.data),

  createStatus: (body: {
    identifier: string;
    name: string;
    description?: string;
    icon?: string;
    countsAsStock?: boolean;
    allowedTransitions?: string[];
  }) => api.post('/inventory-status-catalog', body).then((r) => r.data),

  updateStatus: (
    id: string,
    patch: {
      name?: string;
      description?: string;
      icon?: string;
      countsAsStock?: boolean;
      allowedTransitions?: string[];
    },
  ) => api.patch(`/inventory-status-catalog/${id}`, patch).then((r) => r.data),

  deleteStatus: (id: string) => api.delete(`/inventory-status-catalog/${id}`).then((r) => r.data),
};
